import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Save } from 'lucide-react'
import { clients, type ClientSettings } from '../api'
import { useAuth } from '../auth'

export default function Settings() {
  const auth = useAuth()
  const list = useQuery({ queryKey: ['clients'], queryFn: () => clients.list() })
  const allClients = list.data ?? []
  const [selectedId, setSelectedId] = useState<number | null>(null)

  // Default to the user's own tenant if exposed (single-client list);
  // for superusers the list returns all clients, so picker matters.
  useEffect(() => {
    if (selectedId == null && allClients.length > 0) {
      setSelectedId(allClients[0].id)
    }
  }, [allClients, selectedId])

  const selected = allClients.find((c) => c.id === selectedId) ?? null

  if (auth.status !== 'authenticated') return null
  return (
    <div className="me-scroll mx-auto max-w-3xl overflow-auto p-6">
      <h1 className="text-[18px] font-semibold me-text">Settings</h1>
      <div className="mt-1 text-[12px] me-text-mute">
        Tenant configuration. Set by client admins.
      </div>

      {/* Client picker: visible whenever the list returns more than one
          client (superusers across tenants). For tenant-scoped users
          the list returns a single row and the picker is harmless. */}
      {allClients.length > 1 && (
        <div className="mt-6 me-card p-5">
          <div className="me-section-h">Tenant</div>
          <select
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(Number(e.target.value))}
            className="me-input"
          >
            {allClients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {selected ? <ClientSettingsForm client={selected} /> : (
        <div className="mt-6 me-text-mute text-[12px]">
          {list.isLoading
            ? 'Loading…'
            : auth.user.is_superuser
              ? 'No clients available. Use Django admin to create one.'
              : 'No client assigned to your account.'}
        </div>
      )}
    </div>
  )
}

function ClientSettingsForm({ client }: { client: ClientSettings }) {
  const qc = useQueryClient()
  const [name, setName] = useState(client.name)
  const [storageMode, setStorageMode] = useState(client.default_storage_mode)
  const [retention, setRetention] = useState(client.llm_log_retention)

  useEffect(() => {
    setName(client.name)
    setStorageMode(client.default_storage_mode)
    setRetention(client.llm_log_retention)
  }, [client.id])

  const save = useMutation({
    mutationFn: () => clients.update(client.id, {
      name, default_storage_mode: storageMode, llm_log_retention: retention,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] })
    },
  })

  const isDirty = name !== client.name
    || storageMode !== client.default_storage_mode
    || retention !== client.llm_log_retention

  return (
    <div className="mt-6 space-y-6">
      <div className="me-card p-5">
        <div className="me-section-h">Tenant identity</div>
        <div className="grid grid-cols-[120px_1fr] items-center gap-3">
          <label className="me-mono text-[12px] me-text-dim">code</label>
          <span className="me-mono text-[12.5px] me-text">{client.code}</span>
          <label className="me-mono text-[12px] me-text-dim">name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="me-input" />
        </div>
      </div>

      <div className="me-card p-5">
        <div className="me-section-h">Data residency · default storage mode</div>
        <div className="text-[12px] me-text-mute mb-3">
          How original artifact content is stored. Per-source overrides are configurable in the source-connect flow.
        </div>
        <div className="grid grid-cols-2 gap-2">
          {([
            ['cached', 'Cached', 'Original content stored in our database. Fast access. Works offline.'],
            ['on_demand', 'Fetch on demand', 'Only derived data stored. Original re-fetched from source API when needed. Contractually compliant.'],
          ] as const).map(([val, label, desc]) => (
            <button
              key={val}
              onClick={() => setStorageMode(val)}
              data-selected={storageMode === val ? 'true' : undefined}
              className={`me-card p-3 text-left transition-all ${storageMode === val ? 'ring-2 ring-[color:var(--me-fg)] bg-[color:var(--me-selected)]' : 'hover:bg-[color:var(--me-hover)]'}`}
            >
              <div className="font-medium me-text">{label}</div>
              <div className="mt-1 text-[11.5px] me-text-mute leading-snug">{desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="me-card p-5">
        <div className="me-section-h">LLM call log retention</div>
        <div className="text-[12px] me-text-mute mb-3">
          What to store from each LLM invocation. Stricter policies trade auditability for residency.
        </div>
        <div className="grid grid-cols-2 gap-2">
          {([
            ['full',          'Full',          'Persist input + output forever.'],
            ['redacted',      'Redacted',      'Persist output only. Input redacted right after the call.'],
            ['transient',     'Transient (72h)', 'Persist input + output for 72 hours, then purge.'],
            ['metadata_only', 'Metadata only', 'Token counts + cost + timing. No content.'],
          ] as const).map(([val, label, desc]) => (
            <button
              key={val}
              onClick={() => setRetention(val)}
              data-selected={retention === val ? 'true' : undefined}
              className={`me-card p-3 text-left transition-all ${retention === val ? 'ring-2 ring-[color:var(--me-fg)] bg-[color:var(--me-selected)]' : 'hover:bg-[color:var(--me-hover)]'}`}
            >
              <div className="font-medium me-text">{label}</div>
              <div className="mt-1 text-[11.5px] me-text-mute leading-snug">{desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="me-card p-5">
        <div className="me-section-h">Connected sources</div>
        <div className="text-[12px] me-text-mute">
          Live OAuth-backed Gmail / Drive sync is not enabled in this dev environment. Use <span className="me-mono">manage.py import_from_v1</span> for backfill from the legacy database.
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={!isDirty || save.isPending}
          className="me-btn me-btn-primary"
        >
          <Save size={12} />
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {save.isSuccess && !isDirty && <span className="me-mono text-[11px] text-[color:var(--me-fresh)]">saved</span>}
        {save.error && <span className="text-[12px] text-[color:var(--me-urgent)]">{(save.error as Error).message}</span>}
      </div>
    </div>
  )
}
